/**
 * @file FontCatalogBody.tsx
 * @description Содержимое окна выбора шрифта (phase19.16 Р1, Р1-7; SB99;
 * проект своих шрифтов §1–§5).
 *
 * Один вход для своих шрифтов: первый экран — каталог (`FontCatalogList`:
 * «ВАШИ ШРИФТЫ · N из 5» первыми, ниже шрифты платформы), второй экран того
 * же окна — «Свой шрифт» или «Добавить начертание — …»
 * (`CustomFontUploadPanel`). Каталог на втором экране не размонтируется, а
 * прячется: поиск, фильтр и текст образца сохраняются.
 *
 * Возврат в каталог: после загрузки новый шрифт отмечен, список прокручен к
 * нему и фокус на нём (применяется как обычно кнопкой «Выбрать «…»»); после
 * «Отмена» / «К каталогу» фокус на «Загрузить свой шрифт». Переименование
 * и удаление — вложенными окнами. Окно шрифта элемента (SB120) кладёт в
 * начало списка быстрый выбор («Шрифты сайта», «Недавние») через `leading`.
 */

import { shopFontKey, type SiteFontCategory, type SiteTheme } from "@chaiberi/shared";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { Button } from "../../ui/button";
import { DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useShopFonts } from "../shop-fonts-context";
import { CustomFontUploadPanel } from "./CustomFontUploadPanel";
import { FontCatalogList, type CategoryFilter } from "./FontCatalogList";
import { FontDeleteDialog } from "./FontDeleteDialog";
import { FontRenameDialog } from "./FontRenameDialog";
import { UPLOAD_FONT_BUTTON_SELECTOR } from "./ShopFontsBlock";
import type { ShopFontDto } from "../../../lib/site-view/shop-fonts";
import { cn } from "@/lib/utils";

type Screen = { kind: "catalog" } | { kind: "upload" } | { kind: "addFile"; fontId: string };

interface Props {
  title: string;
  current: string | null;
  initialCategory: CategoryFilter;
  draftFonts: SiteTheme["fonts"] | null;
  /** Перехват Escape окном: `true` — Escape обработан здесь (назад к каталогу). */
  escapeGuard: RefObject<(() => boolean) | null>;
  onClose: () => void;
  onSelect: (key: string) => void;
  onOpenDesigns?: () => void;
  /**
   * Быстрый выбор в начале списка (окно шрифта элемента, SB120: «Шрифты
   * сайта», «Недавние»): получает выбранный ключ и `choose` — отмечает строку,
   * применяется как обычно кнопкой «Выбрать». При поиске скрыт.
   */
  leading?: (chosen: string | null, choose: (key: string) => void) => ReactNode;
}

export function FontCatalogBody({ title, current, initialCategory, draftFonts, escapeGuard, onClose, onSelect, onOpenDesigns, leading }: Props) {
  const shop = useShopFonts();
  const [screen, setScreen] = useState<Screen>({ kind: "catalog" });
  const [chosen, setChosen] = useState<string | null>(current);
  const [renaming, setRenaming] = useState<ShopFontDto | null>(null);
  const [deleting, setDeleting] = useState<ShopFontDto | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Куда вернуть фокус при возврате в каталог. */
  const returnTo = useRef<"upload" | "chosen" | null>(null);
  /** На втором экране выбраны файлы. */
  const uploadDirty = useRef(false);

  const chosenFamily = chosen ? shop.registry.find(chosen)?.family : undefined;
  const addFont = screen.kind === "addFile" ? (shop.items.find((f) => f.id === screen.fontId) ?? null) : null;
  const uploading = screen.kind === "upload" || addFont !== null;
  // Вид нового шрифта по умолчанию — по роли: у рукописной «Рукописный», иначе как у текущего.
  const uploadCategory: SiteFontCategory =
    initialCategory !== "all" ? initialCategory : ((current ? shop.registry.find(current)?.category : undefined) ?? "sans");

  function back(focus: "upload" | "chosen") {
    returnTo.current = focus;
    uploadDirty.current = false;
    setScreen({ kind: "catalog" });
  }

  useEffect(() => {
    escapeGuard.current = () => {
      if (!uploading || !uploadDirty.current) return false;
      back("upload");
      return true;
    };
    return () => {
      escapeGuard.current = null;
    };
  });

  // Возврат в каталог: прокрутить к нужной строке и вернуть на неё фокус.
  useEffect(() => {
    if (uploading || !returnTo.current) return;
    const selector = returnTo.current === "chosen" ? '[role="radio"][aria-checked="true"]' : UPLOAD_FONT_BUTTON_SELECTOR;
    returnTo.current = null;
    const target = listRef.current?.querySelector<HTMLElement>(selector);
    target?.scrollIntoView?.({ block: "nearest" });
    target?.focus();
  }, [uploading]);

  return (
    <>
      {uploading ? (
        <CustomFontUploadPanel
          font={addFont}
          defaultCategory={uploadCategory}
          onDirtyChange={(dirty) => {
            uploadDirty.current = dirty;
          }}
          onBack={() => back("upload")}
          onDone={(font) => {
            setChosen(shopFontKey(font.id));
            back("chosen");
          }}
        />
      ) : (
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
      )}
      <div className={cn("flex min-h-0 flex-1 flex-col gap-3 md:gap-4", uploading && "hidden")}>
        <FontCatalogList
          initialCategory={initialCategory}
          chosen={chosen}
          current={current}
          listRef={listRef}
          leading={leading?.(chosen, setChosen)}
          onChoose={setChosen}
          onUpload={() => setScreen({ kind: "upload" })}
          onAddFile={(font) => setScreen({ kind: "addFile", fontId: font.id })}
          onRename={setRenaming}
          onDelete={setDeleting}
        />
        {/* Кнопки в строку и на телефоне: высота остаётся списку. */}
        <DialogFooter className="shrink-0 flex-row">
          <Button variant="ghost" className="shrink-0" onClick={onClose}>
            Отмена
          </Button>
          <Button className="min-w-0 flex-1 sm:flex-none" disabled={!chosen || !chosenFamily} onClick={() => chosen && onSelect(chosen)}>
            <span className="truncate">{chosenFamily ? `Выбрать «${chosenFamily}»` : "Выбрать"}</span>
          </Button>
        </DialogFooter>
      </div>
      {/* Вложенные окна — внутри родительского, чтобы фокус и закрытие шли по порядку. */}
      <FontRenameDialog font={renaming} onClose={() => setRenaming(null)} />
      <FontDeleteDialog
        font={deleting}
        localFonts={draftFonts}
        onClose={() => setDeleting(null)}
        onDeleted={(font) => {
          if (chosen === shopFontKey(font.id)) setChosen(current === chosen ? null : current);
        }}
        onOpenDesigns={
          onOpenDesigns
            ? () => {
                onClose();
                onOpenDesigns();
              }
            : undefined
        }
      />
    </>
  );
}
